/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Synthesia AI Avatar Video Generation Driver (Task 6.3.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import type {
  VideoGeneratorDriver,
  VideoProviderCode,
  GenerateVideoOptions,
  VideoGenerationResult,
  GeneratedVideo,
  VideoAspectRatio,
} from '../types';

/**
 * Standard model codes associated with Synthesia avatar video generation.
 */
export const SYNTHESIA_MODEL_CODES = [
  'synthesia',
  'synthesia-avatar',
  'talking-avatar',
  'synthesia-talking-avatar',
  'avatar',
];

/**
 * Base credit cost for a standard Synthesia talking avatar video (per 30-second block).
 */
export const SYNTHESIA_BASE_CREDITS = 30;

/**
 * Supported aspect ratios for Synthesia v2 API.
 */
export function resolveSynthesiaAspectRatio(aspectRatio?: VideoAspectRatio): '16:9' | '9:16' | '1:1' {
  if (aspectRatio === '9:16') {
    return '9:16';
  }
  if (aspectRatio === '1:1') {
    return '1:1';
  }
  return '16:9';
}

/**
 * Calculates platform credit cost for Synthesia AI avatar video generation.
 */
export function calculateSynthesiaCredits(durationSeconds?: number): number {
  if (!durationSeconds || durationSeconds <= 30) {
    return SYNTHESIA_BASE_CREDITS;
  }
  const blocks = Math.ceil(durationSeconds / 30);
  return SYNTHESIA_BASE_CREDITS * blocks;
}

/**
 * Video generator driver for Synthesia AI talking avatar videos.
 */
export class SynthesiaVideoDriver implements VideoGeneratorDriver {
  public readonly providerCode: VideoProviderCode = 'synthesia';
  private static readonly SYNTHESIA_BASE_URL = 'https://api.synthesia.io/v2';

  /**
   * Checks whether this driver supports the requested model.
   */
  public supportsModel(model: string): boolean {
    const normalized = model.toLowerCase().trim();
    if (SYNTHESIA_MODEL_CODES.includes(normalized)) {
      return true;
    }
    return normalized.includes('synthesia') || normalized.includes('avatar');
  }

  /**
   * Calculates platform credits required for this video generation.
   */
  public calculateCreditCost(_model: string, durationSeconds?: number): number {
    return calculateSynthesiaCredits(durationSeconds);
  }

  /**
   * Executes Synthesia AI avatar video generation asynchronously.
   */
  public async generateVideo(options: GenerateVideoOptions): Promise<VideoGenerationResult> {
    const startTime = Date.now();

    if (!options.apiKey || !options.apiKey.trim()) {
      throw new Error('Synthesia API key is required for video generation');
    }

    // Extract script text: prompt is the primary dialogue, with extraParams.script fallback
    const scriptText =
      (typeof options.extraParams?.script === 'string' && options.extraParams.script.trim()) ||
      options.prompt.trim();

    if (!scriptText) {
      throw new Error('Script text is required for Synthesia avatar video generation');
    }

    const avatarId =
      (typeof options.extraParams?.avatarId === 'string' && options.extraParams.avatarId.trim()) ||
      'anna_costume1_cameraA';

    const voiceId =
      typeof options.extraParams?.voiceId === 'string' && options.extraParams.voiceId.trim()
        ? options.extraParams.voiceId.trim()
        : undefined;

    const backgroundPreset =
      typeof options.extraParams?.background === 'string'
        ? options.extraParams.background.trim()
        : 'off_white';

    const customBackgroundUrl =
      (typeof options.extraParams?.customBackgroundUrl === 'string' && options.extraParams.customBackgroundUrl.trim()) ||
      (options.imageUrl && options.imageUrl.trim());

    const title =
      (typeof options.extraParams?.title === 'string' && options.extraParams.title.trim()) ||
      (scriptText.length > 50 ? `${scriptText.slice(0, 47)}...` : scriptText);

    const aspectRatio = resolveSynthesiaAspectRatio(options.aspectRatio);

    // Build Synthesia v2 input clip structure
    const clipInput: Record<string, unknown> = {
      avatar: avatarId,
      avatarSettings: {
        horizontalAlign: 'center',
        scale: 1,
        style: 'rectangular',
        seamless: backgroundPreset === 'transparent',
      },
      scriptText,
    };

    if (voiceId) {
      clipInput.voice = voiceId;
    }

    if (customBackgroundUrl) {
      clipInput.background = 'image_url';
      clipInput.backgroundSettings = {
        imageSettings: {
          source: customBackgroundUrl,
        },
      };
    } else if (backgroundPreset === 'green_screen') {
      clipInput.background = 'green_screen';
    } else if (backgroundPreset !== 'transparent') {
      clipInput.background = backgroundPreset;
    }

    const requestPayload: Record<string, unknown> = {
      title,
      description: 'Generated by Sophia Platform Media Studio',
      visibility: 'private',
      aspectRatio,
      input: [clipInput],
      ...(options.webhookUrl ? { webhookUrl: options.webhookUrl } : {}),
      ...(options.extraParams?.synthesiaPayloadOverrides as Record<string, unknown> || {}),
    };

    const authHeader = options.apiKey.startsWith('Bearer ') || options.apiKey.startsWith('Key ')
      ? options.apiKey
      : options.apiKey;

    const headers: Record<string, string> = {
      'Authorization': authHeader,
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(options.gatewayOptions?.customHeaders || {}),
    };

    const submitUrl = `${SynthesiaVideoDriver.SYNTHESIA_BASE_URL}/videos`;

    const submitResponse = await fetch(submitUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestPayload),
      signal: options.abortSignal,
    });

    if (!submitResponse.ok) {
      const errorText = await submitResponse.text().catch(() => '');
      throw new Error(
        `Synthesia video generation submission failed (${submitResponse.status}): ${errorText || submitResponse.statusText}`
      );
    }

    const submitData = (await submitResponse.json()) as Record<string, unknown>;

    // Fast-path: direct video completion returned (e.g. in mocks)
    const directVideo = this.extractVideoFromData(submitData);
    if (directVideo) {
      return {
        video: directVideo,
        model: options.model,
        provider: 'synthesia',
        creditsUsed: this.calculateCreditCost(options.model, options.durationSeconds),
        executionTimeMs: Date.now() - startTime,
        requestId: typeof submitData.id === 'string' ? submitData.id : undefined,
        metadata: submitData,
      };
    }

    const videoId = typeof submitData.id === 'string' ? submitData.id : undefined;
    if (!videoId) {
      throw new Error('Synthesia API did not return a valid video ID');
    }

    // Polling task status: GET /v2/videos/{id}
    const pollIntervalMs = options.pollIntervalMs ?? 1000;
    const maxPollAttempts = options.maxPollAttempts ?? 60;
    const pollUrl = `${SynthesiaVideoDriver.SYNTHESIA_BASE_URL}/videos/${videoId}`;

    let attempts = 0;
    let completedData: Record<string, unknown> | null = null;

    while (attempts < maxPollAttempts) {
      attempts++;

      if (pollIntervalMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
      }

      if (options.abortSignal?.aborted) {
        throw new Error('Synthesia video generation polling was aborted');
      }

      const pollResponse = await fetch(pollUrl, {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Accept': 'application/json',
        },
        signal: options.abortSignal,
      });

      if (!pollResponse.ok) {
        continue;
      }

      const pollData = (await pollResponse.json()) as Record<string, unknown>;
      const status = typeof pollData.status === 'string' ? pollData.status.toLowerCase() : '';

      if (status === 'complete' || status === 'completed') {
        completedData = pollData;
        break;
      }

      if (status === 'failed') {
        const errorMsg = typeof pollData.error === 'string'
          ? pollData.error
          : typeof pollData.message === 'string'
          ? pollData.message
          : JSON.stringify(pollData);
        throw new Error(`Synthesia video generation failed: ${errorMsg}`);
      }
    }

    if (!completedData) {
      throw new Error(`Synthesia video generation timed out after ${attempts} polling attempts`);
    }

    const videoItem = this.extractVideoFromData(completedData);
    if (!videoItem) {
      throw new Error('Completed response from Synthesia did not contain a valid download URL');
    }

    return {
      video: videoItem,
      model: options.model,
      provider: 'synthesia',
      creditsUsed: this.calculateCreditCost(options.model, videoItem.durationSeconds || options.durationSeconds),
      executionTimeMs: Date.now() - startTime,
      requestId: videoId,
      metadata: completedData,
    };
  }

  /**
   * Helper to parse video information from Synthesia response data.
   */
  private extractVideoFromData(data: Record<string, unknown>): GeneratedVideo | null {
    // Standard Synthesia response format: { download: "https://.../video.mp4", duration: 18.5 }
    if (typeof data.download === 'string' && data.download.startsWith('http')) {
      const dimensions = data.dimensions as Record<string, unknown> | undefined;
      return {
        url: data.download,
        contentType: 'video/mp4',
        durationSeconds: typeof data.duration === 'number' ? Math.round(data.duration) : undefined,
        width: typeof dimensions?.width === 'number' ? dimensions.width : undefined,
        height: typeof dimensions?.height === 'number' ? dimensions.height : undefined,
      };
    }

    // Format: { video: { url: "..." } }
    if (data.video && typeof data.video === 'object') {
      const v = data.video as Record<string, unknown>;
      if (typeof v.url === 'string' && v.url.startsWith('http')) {
        return {
          url: v.url,
          contentType: 'video/mp4',
          durationSeconds: typeof v.duration === 'number' ? Math.round(v.duration) : undefined,
        };
      }
    }

    // Format: { url: "https://..." }
    if (typeof data.url === 'string' && data.url.startsWith('http')) {
      return {
        url: data.url,
        contentType: 'video/mp4',
      };
    }

    return null;
  }
}
